/**
 * DynamicSection — renderer generico di una sezione della Relazione Mensile V2.
 *
 * Legge la struttura della sezione dallo `report_schema` (o dallo `schema_snapshot`
 * della relazione se già inviata) e rende dinamicamente i campi.
 *
 * Supporta i seguenti `tipo` di campo:
 *  - number         → FieldNum / FieldNumWithTarget (se `target_key_hotel` presente)
 *  - text           → FieldText (single line)
 *  - textarea       → FieldText area
 *  - enum           → FieldSelect
 *  - bool           → FieldBool
 *  - date           → Input type=date
 *  - tabella_mensile → TabellaMensileWidget (RD4 config)
 *  - array_ripetibile → ArrayRipetibileWidget (RD4 config)
 *
 * Regole:
 *  - I campi con `visibile === false` vengono nascosti.
 *  - I campi con `ruolo_edit` non vuoto vengono mostrati SOLO agli utenti con
 *    ruolo incluso (permette es. "note_interne_direzione" solo per super_admin).
 *  - Il pannello AI (AIDraftPanel) viene aggiunto in fondo se la sezione ha
 *    `ai_include !== false`.
 *  - La sezione `generale` mostra anche AIConclusioniPanel.
 */
import { Input } from "@/components/ui/input";
import {
  FieldNum,
  FieldText,
  FieldSelect,
  FieldBool,
  FieldNumWithTarget,
  AIDraftPanel,
  AIConclusioniPanel,
} from "./sectionHelpers";
import { TabellaMensileWidget } from "./widgets/TabellaMensileWidget";
import { ArrayRipetibileWidget } from "./widgets/ArrayRipetibileWidget";

// Converte enum_options del backend [{value,label}] nel formato tuple [["v","l"], ...]
function enumTuples(opts) {
  if (!Array.isArray(opts)) return [];
  return opts.map((o) => {
    if (Array.isArray(o)) return o;
    return [o.value, o.label ?? o.value];
  });
}

// Il campo deve essere renderizzato per l'utente corrente?
function fieldVisibleForUser(campo, userRole) {
  if (campo.visibile === false) return false;
  const ruoli = campo.ruolo_edit || [];
  if (ruoli.length > 0 && !ruoli.includes(userRole)) return false;
  return true;
}

function DateField({ campo, data, setData, disabled }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{campo.label}</label>
      <Input
        type="date"
        value={data[campo.key] ?? ""}
        disabled={disabled}
        onChange={(e) => setData({ ...data, [campo.key]: e.target.value })}
        data-testid={`field-${campo.key}`}
      />
      {campo.help_text && <p className="text-[10px] text-muted-foreground">{campo.help_text}</p>}
    </div>
  );
}

function renderField(campo, ctx) {
  const { data, setData, isReadOnly, targets, userRole } = ctx;
  if (!fieldVisibleForUser(campo, userRole)) return null;
  const disabled = isReadOnly;
  const key = campo.key;
  const step = campo.step != null ? String(campo.step) : "any";

  switch (campo.tipo) {
    case "number": {
      const target = campo.target_key_hotel ? targets?.[campo.target_key_hotel] : null;
      if (target != null) {
        return (
          <FieldNumWithTarget
            key={key}
            label={campo.label}
            k={key}
            data={data}
            setData={setData}
            step={step}
            suf={campo.placeholder || undefined}
            target={target}
            higherIsBetter
            disabled={disabled}
          />
        );
      }
      return (
        <FieldNum
          key={key}
          label={campo.label}
          k={key}
          data={data}
          setData={setData}
          step={step}
          suf={campo.placeholder || undefined}
          disabled={disabled}
        />
      );
    }
    case "text":
      return <FieldText key={key} label={campo.label} k={key} data={data} setData={setData} disabled={disabled} />;
    case "textarea":
      return <FieldText key={key} label={campo.label} k={key} data={data} setData={setData} disabled={disabled} area />;
    case "enum":
      return (
        <FieldSelect
          key={key}
          label={campo.label}
          k={key}
          data={data}
          setData={setData}
          options={enumTuples(campo.enum_options)}
          disabled={disabled}
        />
      );
    case "bool":
      return <FieldBool key={key} label={campo.label} k={key} data={data} setData={setData} disabled={disabled} />;
    case "date":
      return <DateField key={key} campo={campo} data={data} setData={setData} disabled={disabled} />;
    case "tabella_mensile":
      return (
        <div key={key} className="md:col-span-2">
          <TabellaMensileWidget
            data={data}
            setData={setData}
            isReadOnly={isReadOnly}
            siblings={ctx.siblings}
            meseCorrente={ctx.meseCorrente}
            targets={targets}
            columns={campo.config?.columns}
            userRole={ctx.userRole}
          />
        </div>
      );
    case "array_ripetibile":
      return (
        <ArrayRipetibileWidget
          key={key}
          campoKey={key}
          label={campo.label}
          value={data[key] || []}
          onChange={(next) => setData({ ...data, [key]: next })}
          isReadOnly={isReadOnly}
          config={campo.config}
        />
      );
    default:
      return null;
  }
}

export function DynamicSection({
  sezione,      // schema della sezione: { key, nome, ai_include, campi: [...] }
  data,         // report.sections[sezione.key]
  setData,      // updater
  isReadOnly,
  targets,
  siblings,
  meseCorrente,
  userRole,
  reportId,
}) {
  if (!sezione) return null;
  const campi = (sezione.campi || []).slice().sort((a, b) => (a.ordine || 0) - (b.ordine || 0));

  const ctx = { data, setData, isReadOnly, targets, siblings, meseCorrente, userRole };

  const showAI = sezione.ai_include !== false && sezione.key !== "generale";
  const showConclusioni = sezione.key === "generale";

  // Se la sezione ha un solo campo "tabella_mensile" o "array_ripetibile" molto ampio,
  // saltiamo la grid e lasciamo il widget prendere tutta la larghezza.
  const singleFullWidth = campi.length === 1 && (campi[0].tipo === "tabella_mensile" || campi[0].tipo === "array_ripetibile");

  return (
    <>
      {sezione.descrizione && (
        <p className="text-xs text-muted-foreground mb-4">{sezione.descrizione}</p>
      )}

      {singleFullWidth ? (
        <div>{renderField(campi[0], ctx)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campi.map((c) => renderField(c, ctx))}
        </div>
      )}

      {showConclusioni && (
        <AIConclusioniPanel reportId={reportId} data={data} setData={setData} isReadOnly={isReadOnly} />
      )}

      {showAI && (
        <AIDraftPanel reportId={reportId} section={sezione.key} data={data} setData={setData} isReadOnly={isReadOnly} />
      )}
    </>
  );
}
